/* Homepage-only enhancement. Native card links, pricing and commerce are untouched. */
(() => {
  // Native scrolling remains available. The first interaction stops this band's
  // motion for this page; leaving it, resizing or returning to the tab cannot restart it.
  const enhanceMotion = (host, rails, mobileOnly = false) => {
    const abort = new AbortController(), options = {signal: abort.signal};
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const mobile = matchMedia('(max-width:749px)');
    const help = host.querySelector('.motion-help');
    let alive = true, pageActive = true;
    const tracks = rails.map((rail, index) => {
      const rtl = getComputedStyle(rail).direction === 'rtl';
      const maxPosition = () => Math.max(0, rail.scrollWidth - rail.clientWidth);
      const readPosition = () => Math.max(0, Math.min(maxPosition(), rtl ? -rail.scrollLeft : rail.scrollLeft));
      let visible = false, direction = index % 2 ? -1 : 1;
      let position = readPosition(), previous = 0, raf = 0, timer = 0;
      let idleUntil = performance.now() + 2200;
      const stop = () => { cancelAnimationFrame(raf); clearTimeout(timer); raf = timer = previous = 0; };
      const canRun = () => alive && pageActive && host.isConnected && !host.motionStopped &&
        (!mobileOnly || mobile.matches) && !reduced.matches && visible && !document.hidden && maxPosition() > 0;
      const tick = now => {
        raf = 0;
        if (!canRun()) { previous = 0; return; }
        const dt = previous ? Math.min(now - previous, 40) : 0;
        previous = now;
        const max = maxPosition();
        position = Math.max(0, Math.min(max, position + direction * dt * .009));
        rail.scrollLeft = rtl ? -position : position;
        if ((direction > 0 && position >= max) || (direction < 0 && position <= 0)) {
          direction *= -1; idleUntil = now + 2500; schedule(); return;
        }
        raf = requestAnimationFrame(tick);
      };
      const schedule = () => {
        stop();
        if (!canRun()) return;
        // Start the second photo row at the opposite edge once, without clones.
        // Never reposition it for reduced-motion users or after an interaction.
        if (!rail.motionInitialized) {
          if (direction < 0) rail.scrollLeft = rtl ? -maxPosition() : maxPosition();
          rail.motionInitialized = true;
        }
        position = readPosition();
        const delay = idleUntil - performance.now();
        if (delay > 0) timer = setTimeout(() => { timer = 0; schedule(); }, delay + 1);
        else raf = requestAnimationFrame(tick);
      };
      const semantics = () => {
        if (!mobileOnly || mobile.matches) rail.setAttribute('tabindex', '0');
        else rail.removeAttribute('tabindex');
        if (help?.id) rail.setAttribute('aria-describedby', help.id);
      };
      semantics();
      return {rail, stop, schedule, semantics, setVisible: value => { visible = value; schedule(); }};
    });
    const stopPermanently = () => { host.motionStopped = true; tracks.forEach(track => track.stop()); };
    const scheduleAll = () => tracks.forEach(track => { track.semantics(); track.schedule(); });
    // No preventDefault, pointer capture, drag transforms, or touch-action lock:
    // horizontal swipes, vertical page gestures and native link clicks remain native.
    ['pointerdown', 'touchstart', 'wheel'].forEach(name => host.addEventListener(name, stopPermanently, {...options, passive:true}));
    ['focusin', 'keydown'].forEach(name => host.addEventListener(name, stopPermanently, options));
    host.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') stopPermanently(); }, options);
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => tracks.find(track => track.rail === entry.target)?.setVisible(entry.isIntersecting && entry.intersectionRatio >= .25));
    }, {threshold:[0,.25]});
    tracks.forEach(track => observer.observe(track.rail));
    reduced.addEventListener('change', scheduleAll, options);
    mobile.addEventListener('change', scheduleAll, options);
    window.addEventListener('resize', scheduleAll, {...options, passive:true});
    document.addEventListener('visibilitychange', scheduleAll, options);
    window.addEventListener('pagehide', () => { pageActive = false; tracks.forEach(track => track.stop()); }, options);
    window.addEventListener('pageshow', () => { pageActive = true; scheduleAll(); }, options);
    if (host.contains(document.activeElement)) stopPermanently();
    return () => { alive = false; abort.abort(); observer.disconnect(); tracks.forEach(track => track.stop()); };
  };
  if (!customElements.get('isuvio-home-product-rail')) {
    customElements.define('isuvio-home-product-rail', class extends HTMLElement {
      connectedCallback() {
        this.cleanup?.();
        this.startFrame = requestAnimationFrame(() => this.setup());
      }
      setup() {
        if (!this.isConnected) return;
        const rail = this.querySelector('.t4s-products');
        const title = this.querySelector('.t4s-section-title')?.textContent.trim();
        if (!rail || !title) return;
        rail.setAttribute('role', 'region');
        rail.setAttribute('aria-label', title);
        this.cleanup = enhanceMotion(this, [rail], true);
      }
      disconnectedCallback() { cancelAnimationFrame(this.startFrame); this.cleanup?.(); }
    });
  }
  if (!customElements.get('isuvio-home-community')) {
    customElements.define('isuvio-home-community', class extends HTMLElement {
      connectedCallback() {
        this.cleanup?.();
        this.startFrame = requestAnimationFrame(() => {
          if (!this.isConnected) return;
          const stopPhotos = this.setupPhotos();
          const stopMotion = typeof IntersectionObserver === 'function'
            ? enhanceMotion(this, [...this.querySelectorAll('.photo-window')]) : () => {};
          this.cleanup = () => { stopPhotos(); stopMotion(); };
        });
      }
      setupPhotos() {
        const photos = this.querySelector('.community-photos');
        const mount = () => {
          this.querySelectorAll('template[data-isuvio-community-photo]').forEach(template => {
            template.replaceWith(template.content.cloneNode(true));
          });
        };
        // The existing fixed photo slots preserve both rows before mounting.
        // Browsers without this API keep the native image-loading fallback.
        if (!photos || typeof IntersectionObserver !== 'function') {
          mount();
          return () => {};
        }
        const observer = new IntersectionObserver(entries => {
          if (!entries.some(entry => entry.isIntersecting)) return;
          mount();
          observer.disconnect();
        }, {rootMargin:'400px 0px', threshold:0});
        observer.observe(photos);
        return () => observer.disconnect();
      }
      disconnectedCallback() { cancelAnimationFrame(this.startFrame); this.cleanup?.(); }
    });
  }
  if (!customElements.get('isuvio-home-benefits')) {
    customElements.define('isuvio-home-benefits', class extends HTMLElement {
      connectedCallback() {
        this.cleanup?.();
        this.startFrame = requestAnimationFrame(() => this.setup());
      }
      setup() {
        if (!this.isConnected) return;
        const abort = new AbortController(), options = {signal:abort.signal};
        const reduced = matchMedia('(prefers-reduced-motion: reduce)');
        const cancels = [];
        this.querySelectorAll('[data-isuvio-disclosure]').forEach(detail => {
          const summary = detail.querySelector('summary'), content = detail.querySelector('.detail-body');
          if (!summary || !content) return;
          let animation = null, expanded = detail.open;
          const finish = () => {
            if (animation) { animation.onfinish = null; animation.cancel(); animation = null; }
            detail.open = expanded; detail.dataset.expanded = String(expanded);
          };
          detail.dataset.expanded = String(expanded);
          summary.addEventListener('click', event => {
            if (reduced.matches || !content.animate) return;
            event.preventDefault();
            const start = detail.open ? content.getBoundingClientRect().height : 0;
            if (animation) { animation.onfinish = null; animation.cancel(); }
            expanded = !expanded; detail.open = true; detail.dataset.expanded = String(expanded);
            const end = expanded ? content.scrollHeight : 0;
            animation = content.animate([
              {height:`${start}px`,opacity:expanded?.3:1},
              {height:`${end}px`,opacity:expanded?1:0}
            ], {duration:260,easing:'cubic-bezier(.2,.7,.2,1)',fill:'both'});
            animation.onfinish = finish;
          }, options);
          detail.addEventListener('toggle', () => {
            if (!animation) { expanded = detail.open; detail.dataset.expanded = String(expanded); }
          }, options);
          reduced.addEventListener('change', finish, options);
          cancels.push(finish);
        });
        this.cleanup = () => { abort.abort(); cancels.forEach(cancel => cancel()); };
      }
      disconnectedCallback() { cancelAnimationFrame(this.startFrame); this.cleanup?.(); }
    });
  }
})();
